import { Autocomplete, Chip, TextField, TextFieldProps, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiInput-root:before, & .MuiInput-root:after': {
    display: 'none',
  },
  '& .MuiInputLabel-root': {
    color: `${theme.palette.grey[600]} !important`,
    fontSize: '18px',
    lineHeight: '1rem',
    display: 'inline-block',
  },
  '& .MuiInputBase-root': {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.375),
    marginTop: '24px !important',
    padding: '8px 12px !important',
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
  '& ::-webkit-input-placeholder': {
    color: `${theme.palette.text.secondary} !important`,
    opacity: '1 !important',
  },
  '& .MuiInputBase-root.Mui-focused': {
    backgroundColor: theme.palette.background.paper,
    borderColor: alpha(theme.palette.primary.main, 0.6),
    outline: 0,
    boxShadow: `0 0 0 0.2rem ${alpha(theme.palette.primary.main, 0.25)}`,
  },
}));

export type SimpleChipsInputProps = Omit<TextFieldProps, 'onChange'> & {
  value: string[];
  options?: string[];
  separator?: string | string[];
  validateOption?: (option: string) => boolean;
  /** When true, input is not editable but not visually disabled (readonly). */
  readOnly?: boolean;
  onChange: (event: React.SyntheticEvent, value: string[]) => void;
};

export function SimpleChipsInput({
  value,
  options,
  separator = [',', ' '],
  onChange,
  onBlur,
  validateOption,
  readOnly,
  ...rest
}: SimpleChipsInputProps) {
  const separators = Array.isArray(separator) ? separator : [separator];
  const [inputValue, setInputValue] = useState('');

  const parseItems = (value: string) => {
    let result = [value];

    // Split by each separator in sequence
    separators.forEach((sep) => {
      result = result.flatMap((item) => item?.split(sep) ?? []);
    });

    return result.map((s) => s?.trim()).filter(Boolean);
  };

  return (
    <StyledAutocomplete
      multiple
      freeSolo
      selectOnFocus
      disableClearable
      clearOnBlur
      options={options || []}
      value={value}
      inputValue={inputValue}
      disabled={rest.disabled}
      readOnly={readOnly}
      open={readOnly ? false : undefined}
      onChange={(event, value) => {
        if (validateOption) {
          value = (value as string[]).filter((item: string) => validateOption(item));
        }

        onChange(event, value as string[]);
      }}
      onBlur={(event) => {
        const items = parseItems((event.target as unknown as { value: string }).value);

        if (items.length) {
          onChange && onChange(event, [...(value || []), ...items]);
        }

        onBlur && onBlur(event as any);
      }}
      onInputChange={(event, newInputValue) => {
        const newOptionCreated = separators.some(
          (sep) => newInputValue.endsWith(sep) && !!newInputValue.slice(0, -sep.length).trim(),
        );

        if (newOptionCreated && (!validateOption || validateOption(newInputValue))) {
          onChange(event, [...(value || []), ...parseItems(newInputValue)]);
        } else {
          setInputValue(newInputValue);
        }
      }}
      renderTags={(value, getTagProps) =>
        (value as string[]).map((option, index) => {
          return <Chip label={option} {...getTagProps({ index })} />;
        })
      }
      renderOption={(props, option) => {
        return (
          <li {...props} key={option as string}>
            {option as React.ReactNode}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...rest}
          {...params}
          placeholder={!value.length ? rest.placeholder : undefined}
          InputLabelProps={{ shrink: true }}
          variant={'standard'}
          inputProps={{
            ...params.inputProps,
            readOnly: readOnly ?? false,
          }}
        />
      )}
    ></StyledAutocomplete>
  );
}
